import {
  Adjustment as AdjustmentApi,
  CardType,
  PaymentStatus as PaymentStatusApi,
  PayoutStatus as RetailPayoutStatusApi,
  PayoutType as RetailPayoutTypeApi,
  RetailPayment as RetailPaymentApi,
  RetailPaymentACHDetails,
  RetailPaymentCardDetails as RetailPaymentCardDetailsApi,
  RetailPaymentReferenceType as RetailPaymentReferenceTypeApi,
  RetailPayout as RetailPayoutApi,
  RetailRefund as RetailRefundApi,
  RetailRefundFailureReason as ApiFailureReason,
  RetailRefundReason as ApiRefundReason,
  RetailRefundStatus as ApiRefundStatus,
  RetailTransaction as RetailTransactionApi,
  RetailPaymentPaymentMethodDetailsType as RetailPaymentPaymentMethodDetailsTypeApi,
} from '@vendasta/billing';
import { Dispute, disputeFromApi } from './dispute';
import { PaymentCardType } from '../payment-card';

export enum RetailPaymentStatus {
  Failed = 'Failed',
  Succeeded = 'Succeeded',
  Refunded = 'Refunded',
  PartialyRefunded = 'Partially refunded',
  Disputed = 'Disputed',
}

export enum RetailRefundReason {
  Unset = 'Unset',
  Duplicate = 'Duplicate',
  Fraudulent = 'Fraudulent',
  RequestedByCustomer = 'Requested by customer',
}

export enum RetailRefundFailureReason {
  Unset = 'Unset',
  LostOrStolenCard = 'Lost or stolen card',
  ExpiredOrCanceledCard = 'Expired or canceled card',
  Unknown = 'Unknown',
}

export enum RetailRefundStatus {
  Unset = 'Unset',
  Pending = 'Pending',
  Succeeded = 'Succeeded',
  Failed = 'Failed',
  RequiresAction = 'Requires action',
  Canceled = 'Canceled',
}

export enum RetailPayoutStatus {
  Unset = 'Unset',
  Paid = 'Paid',
  Pending = 'Pending',
  InTransit = 'In transit',
  Canceled = 'Canceled',
  Failed = 'Failed',
}

export enum RetailPayoutType {
  Unset = 'Unset',
  BankAccount = 'Bank Account',
  Card = 'Card',
}

export enum RetailPaymentMethodType {
  Unset = 'unset',
  ACHDebit = 'ach_debit',
  Card = 'card',
  ACSSDebit = 'acss_debit',
}

export enum RetailPaymentErrorCode {
  AccountClosed = 'no_account',
  InsufficientFunds = 'insufficient_funds',
  DebitNotAuthorized = 'debit_not_authorized',
  Unknown = 'unknown',
  Unset = '',
}

export function isRefundableStatus(status: RetailPaymentStatus): boolean {
  return [RetailPaymentStatus.Succeeded, RetailPaymentStatus.PartialyRefunded].includes(status);
}

export function retailRefundReasonFromApi(r: ApiRefundReason): RetailRefundReason | undefined {
  switch (r) {
    case ApiRefundReason.RETAIL_REFUND_REASON_UNSET:
      return RetailRefundReason.Unset;
    case ApiRefundReason.RETAIL_REFUND_REASON_DUPLICATE:
      return RetailRefundReason.Duplicate;
    case ApiRefundReason.RETAIL_REFUND_REASON_FRAUDULENT:
      return RetailRefundReason.Fraudulent;
    case ApiRefundReason.RETAIL_REFUND_REASON_REQUESTED_BY_CUSTOMER:
      return RetailRefundReason.RequestedByCustomer;
    default:
      return undefined;
  }
}

export function retailRefundReasonToApi(r: RetailRefundReason): ApiRefundReason {
  switch (r) {
    case RetailRefundReason.Duplicate:
      return ApiRefundReason.RETAIL_REFUND_REASON_DUPLICATE;
    case RetailRefundReason.Fraudulent:
      return ApiRefundReason.RETAIL_REFUND_REASON_FRAUDULENT;
    case RetailRefundReason.RequestedByCustomer:
      return ApiRefundReason.RETAIL_REFUND_REASON_REQUESTED_BY_CUSTOMER;
    default:
      return ApiRefundReason.RETAIL_REFUND_REASON_UNSET;
  }
}

export function retailRefundFailureReasonFromApi(r: ApiFailureReason): RetailRefundFailureReason | undefined {
  switch (r) {
    case ApiFailureReason.RETAIL_REFUND_FAILURE_REASON_UNSET:
      return RetailRefundFailureReason.Unset;
    case ApiFailureReason.RETAIL_REFUND_FAILURE_REASON_LOST_OR_STOLEN_CARD:
      return RetailRefundFailureReason.LostOrStolenCard;
    case ApiFailureReason.RETAIL_REFUND_FAILURE_REASON_EXPIRED_OR_CANCELED_CARD:
      return RetailRefundFailureReason.ExpiredOrCanceledCard;
    case ApiFailureReason.RETAIL_REFUND_FAILURE_REASON_UNKNOWN:
      return RetailRefundFailureReason.Unknown;
    default:
      return undefined;
  }
}

export function retailRefundStatusFromApi(r: ApiRefundStatus): RetailRefundStatus | undefined {
  switch (r) {
    case ApiRefundStatus.RETAIL_REFUND_STATUS_UNSET:
      return RetailRefundStatus.Unset;
    case ApiRefundStatus.RETAIL_REFUND_STATUS_PENDING:
      return RetailRefundStatus.Pending;
    case ApiRefundStatus.RETAIL_REFUND_STATUS_SUCCEEDED:
      return RetailRefundStatus.Succeeded;
    case ApiRefundStatus.RETAIL_REFUND_STATUS_FAILED:
      return RetailRefundStatus.Failed;
    case ApiRefundStatus.RETAIL_REFUND_STATUS_REQUIRES_ACTION:
      return RetailRefundStatus.RequiresAction;
    case ApiRefundStatus.RETAIL_REFUND_STATUS_CANCELED:
      return RetailRefundStatus.Canceled;
    default:
      return undefined;
  }
}

export interface CardDetails {
  cardType: PaymentCardType;
  lastFourDigits: string;
}

export interface PaymentMethodTypeDetails {
  lastFourDigits: string;
}

export interface RetailRefund {
  id: string;
  amount: number;
  currency: string;
  refundReason?: RetailRefundReason;
  status?: RetailRefundStatus;
  failureReason?: RetailRefundFailureReason;
  created?: Date;
  payment?: RetailPayment;
}

export interface RetailPayment {
  id: string;
  merchantId: string;
  created: Date;
  currencyCode: string;
  amount: number;
  referenceId: string;
  referenceType: RetailPaymentReferenceTypeApi;
  description: string;
  customerId: string;
  status?: RetailPaymentStatus;
  failureCode: string;
  failureMessage: string;
  applicationFee: number;
  dispute?: Dispute;
  refunds: RetailRefund[];
  paymentMethodDetailsType: RetailPaymentMethodType;
  cardDetails?: CardDetails;
  achDetails?: PaymentMethodTypeDetails;
  acssDetails?: PaymentMethodTypeDetails;
}

export interface RetailPayout {
  id: string;
  arrivalDate: Date;
  amount: number;
  currencyCode: string;
  status: RetailPayoutStatus;
  type: RetailPayoutType;
  lastFourDigits: string;
  bankName: string;
  bankId: string;
  failureCode: string;
  failureMessage: string;
}

export interface Adjustment {
  id: string;
  amount: number;
  currency: string;
  description: string;
  fee: number;
  net: number;
  refund?: RetailRefund;
  dispute?: Dispute;
  created: Date;
}

export enum RetailTransactionType {
  payment = 'payment',
  refund = 'refund',
}

export interface RetailTransaction {
  payment?: RetailPayment;
  refund?: RetailRefund;
  payoutFailure?: RetailPayout;
  adjustment?: Adjustment;
}

export function retailPaymentFromApi(pApi: RetailPaymentApi): RetailPayment {
  return {
    id: pApi.id || '',
    merchantId: pApi.merchantId || '',
    created: pApi.created || null,
    currencyCode: pApi.currencyCode || '',
    amount: pApi.amount || 0.0,
    referenceId: pApi.referenceId || '',
    referenceType: pApi.referenceType,
    description: pApi.description || '',
    customerId: pApi.customerId || '',
    status: retailPaymentStatusFromApi(pApi.status, pApi.disputed),
    failureCode: pApi.failureCode,
    failureMessage: pApi.failureMessage,
    applicationFee: pApi.applicationFee,
    dispute: disputeFromApi(pApi.dispute),
    refunds: retailRefundsfromApi(pApi.refunds),
    cardDetails: pApi.cardDetails ? cardDetailsFromApi(pApi.cardDetails) : undefined,
    paymentMethodDetailsType: retailPaymentTypeFromApi(pApi.paymentMethodDetailsType),
    achDetails: pApi.achDebitDetails ? achDetailsFromApi(pApi.achDebitDetails) : undefined,
    acssDetails: pApi.acssDebitDetails ? acssDetailsFromApi(pApi.acssDebitDetails) : undefined,
  };
}

export function retailRefundsfromApi(refunds: RetailRefundApi[]): RetailRefund[] {
  return (refunds || []).map((refund: RetailRefundApi) => retailRefundFromApi(refund));
}

export function retailRefundFromApi(refund: RetailRefundApi): RetailRefund {
  return {
    id: refund.id || '',
    amount: refund.amount || 0,
    currency: refund.currencyCode || '',
    refundReason: retailRefundReasonFromApi(refund.refundReason),
    status: retailRefundStatusFromApi(refund.status),
    failureReason: retailRefundFailureReasonFromApi(refund.failureReason),
    created: refund.created ? new Date(refund.created) : undefined,
    payment: refund.payment ? retailPaymentFromApi(refund.payment) : undefined,
  };
}

export function retailPayoutFromApi(payout: RetailPayoutApi): RetailPayout {
  return {
    id: payout.id,
    arrivalDate: payout.arrivalDate,
    amount: payout.amount,
    currencyCode: payout.currencyCode,
    status: retailPayoutStatusFromApi(payout.status),
    type: retailPayoutTypeFromApi(payout.type),
    lastFourDigits: payout.lastFourDigits,
    bankName: payout.bankName,
    bankId: payout.bankId,
    failureCode: payout.failureCode,
    failureMessage: payout.failureMessage,
  };
}

export function retailPayoutStatusFromApi(status: RetailPayoutStatusApi): RetailPayoutStatus {
  switch (status) {
    case RetailPayoutStatusApi.PAYOUT_STATUS_PAID:
      return RetailPayoutStatus.Paid;
    case RetailPayoutStatusApi.PAYOUT_STATUS_PENDING:
      return RetailPayoutStatus.Pending;
    case RetailPayoutStatusApi.PAYOUT_STATUS_IN_TRANSIT:
      return RetailPayoutStatus.InTransit;
    case RetailPayoutStatusApi.PAYOUT_STATUS_CANCELED:
      return RetailPayoutStatus.Canceled;
    case RetailPayoutStatusApi.PAYOUT_STATUS_FAILED:
      return RetailPayoutStatus.Failed;
    default:
      return RetailPayoutStatus.Unset;
  }
}

export function retailPayoutTypeFromApi(t: RetailPayoutTypeApi): RetailPayoutType {
  switch (t) {
    case RetailPayoutTypeApi.PAYOUT_TYPE_BANK_ACCOUNT:
      return RetailPayoutType.BankAccount;
    case RetailPayoutTypeApi.PAYOUT_TYPE_CARD:
      return RetailPayoutType.Card;
    default:
      return RetailPayoutType.Unset;
  }
}

export function retailPaymentTypeFromApi(t: RetailPaymentPaymentMethodDetailsTypeApi): RetailPaymentMethodType {
  switch (t) {
    case RetailPaymentPaymentMethodDetailsTypeApi.PAYMENT_METHOD_DETAILS_TYPE_CARD:
      return RetailPaymentMethodType.Card;
    case RetailPaymentPaymentMethodDetailsTypeApi.PAYMENT_METHOD_DETAILS_TYPE_ACH_DEBIT:
      return RetailPaymentMethodType.ACHDebit;
    case RetailPaymentPaymentMethodDetailsTypeApi.PAYMENT_METHOD_DETAILS_TYPE_ACSS_DEBIT:
      return RetailPaymentMethodType.ACSSDebit;
    default:
      return RetailPaymentMethodType.Unset;
  }
}

export function adjustmentFromApi(adjustment: AdjustmentApi): Adjustment {
  return {
    amount: adjustment.amount,
    currency: adjustment.currency,
    description: adjustment.description,
    dispute: disputeFromApi(adjustment.dispute),
    fee: adjustment.fee,
    id: adjustment.id,
    net: adjustment.net,
    refund: adjustment.refund ? retailRefundFromApi(adjustment.refund) : undefined,
    created: adjustment.created,
  };
}

export function retailTransactionFromApi(transaction: RetailTransactionApi): RetailTransaction {
  return {
    payment: transaction.payment ? retailPaymentFromApi(transaction.payment) : undefined,
    refund: transaction.refund ? retailRefundFromApi(transaction.refund) : undefined,
    payoutFailure: transaction.payoutFailure ? retailPayoutFromApi(transaction.payoutFailure) : undefined,
    adjustment: transaction.adjustment ? adjustmentFromApi(transaction.adjustment) : undefined,
  };
}

function cardDetailsFromApi(cardDetails: RetailPaymentCardDetailsApi): CardDetails {
  return {
    lastFourDigits: cardDetails.lastFourDigits,
    cardType: cardTypeFromApi(cardDetails.cardType),
  };
}

function achDetailsFromApi(achDetails: RetailPaymentACHDetails): PaymentMethodTypeDetails {
  return {
    lastFourDigits: achDetails.lastFourDigits,
  };
}

function acssDetailsFromApi(acssDetails: RetailPaymentACHDetails): PaymentMethodTypeDetails {
  return {
    lastFourDigits: acssDetails.lastFourDigits,
  };
}

function cardTypeFromApi(cardType: CardType): PaymentCardType {
  switch (cardType) {
    case CardType.CARD_TYPE_VISA:
      return PaymentCardType.VISA;
    case CardType.CARD_TYPE_MASTERCARD:
      return PaymentCardType.MASTERCARD;
    case CardType.CARD_TYPE_AMEX:
      return PaymentCardType.AMEX;
    case CardType.CARD_TYPE_DISCOVER:
      return PaymentCardType.DISCOVER;
    case CardType.CARD_TYPE_JCB:
      return PaymentCardType.JCB;
    case CardType.CARD_TYPE_DINERS_CLUB:
      return PaymentCardType.DINERS_CLUB;
    default:
      return PaymentCardType.UNKNOWN;
  }
}

export function retailPaymentStatusFromApi(
  status: PaymentStatusApi,
  disputed: boolean,
): RetailPaymentStatus | undefined {
  if (disputed) {
    return RetailPaymentStatus.Disputed;
  }
  switch (status) {
    case PaymentStatusApi.PAYMENT_STATUS_SUCCEEDED:
      return RetailPaymentStatus.Succeeded;
    case PaymentStatusApi.PAYMENT_STATUS_FAILED:
      return RetailPaymentStatus.Failed;
    case PaymentStatusApi.PAYMENT_STATUS_REFUNDED:
      return RetailPaymentStatus.Refunded;
    case PaymentStatusApi.PAYMENT_STATUS_PARTIALLY_REFUNDED:
      return RetailPaymentStatus.PartialyRefunded;
    default:
      return undefined;
  }
}
